import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import Login from "@/pages/Login";
import Today from "@/pages/Today";
import Program from "@/pages/Program";
import Phase from "@/pages/Phase";
import Session from "@/pages/Session";
import Profile from "@/pages/Profile";
import Select from "@/pages/Select";
import { getActiveProgramId } from "@/data/program";
import { Loader2 } from "lucide-react";

function Loading() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0a0a0a]">
      <Loader2 className="animate-spin text-[#fa003f]" size={32} />
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Layout>{children}</Layout>;
}

// Auth required but no Layout / no program gate (used for the program picker)
function AuthOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Requires an active program selected; otherwise redirect to the picker
function RequireProgram({ children }) {
  if (!getActiveProgramId()) return <Navigate to="/select" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <Login />
              </PublicOnly>
            }
          />
          <Route
            path="/select"
            element={
              <AuthOnly>
                <Select />
              </AuthOnly>
            }
          />
          <Route
            path="/"
            element={
              <Protected>
                <RequireProgram>
                  <Today />
                </RequireProgram>
              </Protected>
            }
          />
          <Route
            path="/program"
            element={
              <Protected>
                <RequireProgram>
                  <Program />
                </RequireProgram>
              </Protected>
            }
          />
          <Route
            path="/program/:phaseId"
            element={
              <Protected>
                <RequireProgram>
                  <Phase />
                </RequireProgram>
              </Protected>
            }
          />
          <Route
            path="/session/:dayId"
            element={
              <Protected>
                <RequireProgram>
                  <Session />
                </RequireProgram>
              </Protected>
            }
          />
          <Route
            path="/profile"
            element={
              <Protected>
                <RequireProgram>
                  <Profile />
                </RequireProgram>
              </Protected>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
